import React, { useState, useEffect, useRef } from 'react';
import AppConst from 'appConstants';
import { connect, useDispatch } from 'react-redux';
import { Information16 } from '@carbon/icons-react';
import { RootState } from 'redux/root.reducer';
import { useFormik, FormikTouched } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  APP_URL,
  CFCProps,
  SCREEN_SIZE,
  LOCATION_TYPES_CODE,
  OptionItem
} from 'types';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { SearchAutoSuggest } from 'components';
import {
  Button,
  Toggle,
  Select,
  SelectItem,
  TextArea,
  TextInput,
  InlineLoading,
  RadioButtonGroup,
  RadioButton,
  MultiSelect
} from 'carbon-components-react';
import { useComponentMount } from 'utilities/hooks.util';
import { fetchMemberStart } from 'redux/members';
import {
  fetchLodgeEnquiryInit,
  saveEnquiryPresetsStart,
  deleteEnquiryPresetsStart,
  clearEnquiryPresets,
  returnFromEnquiryPresets
} from 'redux/lodgeEnquiry';
import { orderBy, uniq, isEmpty, keys, values as getValues } from 'lodash';
import { RETIRED_ACTIVITY_TYPES } from 'appConstants/enquiry';

const { workPlaceLocItems } = AppConst.enquiry;

type fullOrgOptionItem = {
  tenant: number;
  value: string;
  label: string;
};

const defaultValues = {
  refresh: true,
  authorityId: '',
  lodgeEnquiryType: '',
  userReference: '',
  notes: '',
  authorityType: '',
  otherAuthorityName: '',
  activityTypes: [],
  locationTypes: [],
  locationsInRoad: [],
  stateFilter: 'all',
  isPlanningJob: null,
  alwaysStart: false
};

export const EnquiryPresets: React.FC<Props> = ({
  user,
  status,
  orgs,
  screenSize,
  designAct,
  excavationAct
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [exActivityTypeItems, setExActivityTypeItems] = useState<any>([]);
  const [designActivityTypeItems, setDesignActivityTypeItems] = useState<any>(
    []
  );
  const [fullLocalGovOrgItems, setFullLocalGovOrgItems] = useState<
    fullOrgOptionItem[]
  >([]);
  const [fullUtilOrgItems, setFullUtilOrgItems] = useState<fullOrgOptionItem[]>(
    []
  );
  const [localGovOrgItems, setLocalGovOrgItems] = useState<OptionItem[]>([]);
  const [utilOrgItems, setUtilOrgItems] = useState<OptionItem[]>([]);
  const [localGovStateItems, setLocalGovStateItems] = useState<OptionItem[]>(
    []
  );
  const [utilStateItems, setUtilStateItems] = useState<OptionItem[]>([]);
  const [stateFilter, setStateFilter] = useState<number | undefined>();
  const [loadedPastData, setLoadedPastData] = useState<boolean>(false);
  const resetPresetsCache = useRef<boolean>(true);

  const {
    touched,
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setValues,
    resetForm,
    handleSubmit
  } = useFormik({
    enableReinitialize: true,
    initialValues: defaultValues,
    onSubmit: (values: any) => {
      const valueForSubmit = {
        ...values,
        authorityId:
          values.authorityType === 'other' || values.authorityType === 'private'
            ? values.authorityType
            : values.authorityId,
        isPlanningJob:
          values.lodgeEnquiryType !== ''
            ? values.lodgeEnquiryType === 'Design'
            : null,
        stateFilter: 'all'
      };
      dispatch(saveEnquiryPresetsStart(valueForSubmit));
    }
  });
  //const [localGovStateItems, setLocalGovStateItems] = useState<OptionItem[]>([]);
  //const [utilStateItems, setUtilStateItems] = useState<OptionItem[]>([]);
  const getAuthorityInput = (type: string) => {
    switch (type) {
      case 'local':
      case 'utilities': {
        return (
          <>
            <Select
              id="stateFilter"
              className="state-filter"
              name="stateFilter"
              size="xl"
              placeholder="Select"
              labelText="Region filter"
              value={values.stateFilter}
              onChange={handleStateFilter}
              onBlur={handleBlur}
            >
              <SelectItem text="All" value="all" />
              {type === 'local'
                ? localGovStateItems.map((item: any, index: number) => {
                    return (
                      <SelectItem
                        key={`state-filter-${index}`}
                        text={item.label}
                        value={item.value}
                      />
                    );
                  })
                : utilStateItems.map((item: any, index: number) => {
                    return (
                      <SelectItem
                        key={`state-filter-${index}`}
                        text={item.label}
                        value={item.value}
                      />
                    );
                  })}
            </Select>
            <SearchAutoSuggest
              id="authority"
              maxItem={15}
              placeHolderText="Your input ..."
              labelText={type === 'local' ? 'Local authorities' : 'Utilities'}
              size="xl"
              items={type === 'local' ? localGovOrgItems : utilOrgItems}
              defaultInputValue={getAuthorityValue(
                values.authorityId,
                type === 'local' ? localGovOrgItems : utilOrgItems
              )}
              onSelectItem={handleSearchChange}
              onChangeInput={handleSearchInputChange}
            />
          </>
        );
      }
      case 'other': {
        return (
          <TextInput
            id="otherAuthorityName"
            size="xl"
            name="otherAuthorityName"
            className="other-name-input"
            labelText="Other (name)"
            data-testid="otherAuthorityName"
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={100}
            defaultValue={values.otherAuthorityName}
          />
        );
      }
      default:
        return <></>;
    }
  };

  const getAuthorityValue = (id: string | number, items: OptionItem[]) => {
    const selectedItem = items.find((item) => item.value === id);
    return selectedItem?.label || '';
  };

  const getDefaultWorkPlaceLoc = (values: any, workPlaceLocItems: any[]) => {
    let selectedItems = [];
    selectedItems = workPlaceLocItems.filter((item) =>
      values.locationsInRoad.includes(item.value)
    );
    const privateLocation = workPlaceLocItems.find(
      (item) => item.id === 'private'
    );
    if (values.locationTypes.includes(LOCATION_TYPES_CODE.PRIVATE)) {
      selectedItems.push(privateLocation);
    }

    return selectedItems;
  };

  const getDefaultActivity = (values: any, items: any[]) => {
    if (
      !Array.isArray(values.activityTypes) ||
      values.activityTypes.length === 0
    ) {
      return [];
    }

    const hasRetiredExcavationActivity =
      values.lodgeEnquiryType === 'Excavation' &&
      values.activityTypes.some((activityType: string) =>
        RETIRED_ACTIVITY_TYPES.includes(activityType)
      );

    if (hasRetiredExcavationActivity) {
      return [];
    }

    const selectedItems = items.filter((item) =>
      values.activityTypes.includes(item.id)
    );

    return selectedItems;
  };

  const handleStateFilter = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setFieldValue('stateFilter', evt.target.value);
    setFieldValue('authorityId', '');
    setFieldValue('refresh', true);
  };

  const handleSearchChange = (item: OptionItem) => {
    setFieldValue('authorityId', item.value);
    setFieldTouched('authorityId', true);
  };

  const handleSearchInputChange = (value: string) => {
    if (!value) {
      setFieldValue('authorityId', '');
    }
  };

  const handleWorkLocChange = ({ selectedItems }: { selectedItems: any[] }) => {
    const locationTypes = [];
    const roadLocation = selectedItems
      .filter((item) => item.id !== 'private')
      .map((item) => item.value);
    const privateLocation = selectedItems.find((item) => item.id === 'private');
    if (roadLocation.length > 0) {
      locationTypes.push(LOCATION_TYPES_CODE.ROAD);
    }
    if (privateLocation) {
      locationTypes.push(LOCATION_TYPES_CODE.PRIVATE);
    }
    setFieldValue('locationTypes', locationTypes);
    setFieldValue('locationsInRoad', roadLocation);
    setFieldTouched('locationTypes', true);
    setFieldTouched('locationsInRoad', true);
  };

  const handleActivityChange = ({
    selectedItems
  }: {
    selectedItems: any[];
  }) => {
    const activityTypes = selectedItems.map((item) => item.id);
    setFieldValue('activityTypes', activityTypes);
    setFieldTouched('activityTypes', true);
  };

  const handleLodgeEnquiryTypeChange = (val: React.ReactText) => {
    setFieldValue('lodgeEnquiryType', val);
    setFieldValue('activityTypes', []);
    setFieldTouched('lodgeEnquiryType', true);
  };

  const handleAuthorityTypeChange = (val: React.ReactText) => {
    setFieldValue('authorityType', val);
    setFieldValue('authorityId', '');
    setFieldTouched('authorityType', true);
    setFieldValue('stateFilter', 'all');
    setFieldValue('refresh', true);
    setFieldValue('otherAuthorityName', '');
  };

  const handleClearLodgeEnquiryType = () => {
    setFieldValue('lodgeEnquiryType', '');
    setFieldValue('activityTypes', []);
    setFieldTouched('lodgeEnquiryType', false);
    setFieldValue('refresh', true);
  };

  const handleClearAuthorityType = () => {
    setFieldValue('authorityType', '');
    setFieldValue('authorityId', '');
    setFieldTouched('authorityType', false);
    setFieldValue('stateFilter', 'all');
    setFieldValue('refresh', true);
    setFieldValue('otherAuthorityName', '');
  };

  const canSave = (
    values: any,
    touched: FormikTouched<any>,
    loadedPastData: boolean
  ) => {
    if (isEmpty(touched) || !getValues(touched).includes(true)) {
      return false;
    }

    const touchedKeys = keys(touched);
    if (
      touchedKeys.length === 1 &&
      touchedKeys[0] === 'alwaysStart' &&
      !loadedPastData
    ) {
      return false;
    }

    if (values.authorityType === 'other' && !values.otherAuthorityName) {
      return false;
    }

    if (
      (values.authorityType === 'utilities' ||
        values.authorityType === 'local') &&
      !values.authorityId
    ) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    const designActOptions =
      Array.isArray(designAct) &&
      designAct?.length > 0 &&
      designAct.map((item: any) => ({
        id: item.code,
        label: item.label
      }));
    const excavationActOptions =
      Array.isArray(excavationAct) &&
      excavationAct?.length > 0 &&
      excavationAct
        .filter((item: any) => !RETIRED_ACTIVITY_TYPES.includes(item.code))
        .map((item: any) => ({
          id: item.code,
          label: item.label
        }));
    setDesignActivityTypeItems(designActOptions || []);
    setExActivityTypeItems(excavationActOptions || []);
  }, [designAct, excavationAct]);

  useEffect(() => {
    // Clear all preset activities when an Excavation preset uses a retired activity code.
    if (
      values.lodgeEnquiryType !== 'Excavation' ||
      !Array.isArray(values.activityTypes) ||
      values.activityTypes.length === 0
    ) {
      return;
    }

    const hasRetiredExcavationActivity = values.activityTypes.some(
      (activityType: string) => RETIRED_ACTIVITY_TYPES.includes(activityType)
    );

    if (hasRetiredExcavationActivity) {
      setFieldValue('activityTypes', []);
    }
  }, [values.activityTypes, values.lodgeEnquiryType, setFieldValue]);

  useEffect(() => {
    const localAuthority: fullOrgOptionItem[] = [];
    const utility: fullOrgOptionItem[] = [];
    const localTenant: OptionItem[] = [];
    const utilTenant: OptionItem[] = [];
    if (Array.isArray(orgs) && orgs.length > 0) {
      orgs.map((item: any) => {
        const { id, name, organisationType, Tenant } = item;
        if (organisationType === 'LOCAL GOVERNMENT') {
          if (Tenant && !localTenant.find((t) => t.value === Tenant.id)) {
            localTenant.push({
              value: Tenant.id,
              label: Tenant.name.replace('DBYD ', '')
            });
          }
          return localAuthority.push({
            value: id,
            label: name,
            tenant: Tenant?.id
          });
        } else {
          if (Tenant && !utilTenant.find((t) => t.value === Tenant.id)) {
            utilTenant.push({
              value: Tenant.id,
              label: Tenant.name.replace('DBYD ', '')
            });
          }
          return utility.push({ value: id, label: name, tenant: Tenant?.id });
        }
      });
      setLocalGovStateItems(orderBy(uniq(localTenant), ['label'], ['asc']));
      setUtilStateItems(orderBy(uniq(utilTenant), ['label'], ['asc']));
      setFullLocalGovOrgItems(orderBy(localAuthority, ['label'], ['asc']));
      setFullUtilOrgItems(orderBy(utility, ['label'], ['asc']));
    }
  }, [orgs]);

  useEffect(() => {
    if (
      Array.isArray(fullLocalGovOrgItems) &&
      fullLocalGovOrgItems.length > 0
    ) {
      if (stateFilter) {
        const newSet = fullLocalGovOrgItems.filter(
          (item) => item.tenant === stateFilter
        );
        setLocalGovOrgItems(newSet);
      } else {
        setLocalGovOrgItems(fullLocalGovOrgItems);
      }
    }
  }, [fullLocalGovOrgItems, stateFilter]);

  useEffect(() => {
    if (Array.isArray(fullUtilOrgItems) && fullUtilOrgItems.length > 0) {
      if (stateFilter) {
        const newSet = fullUtilOrgItems.filter(
          (item) => item.tenant === stateFilter
        );
        setUtilOrgItems(newSet);
      } else {
        setUtilOrgItems(fullUtilOrgItems);
      }
    }
  }, [fullUtilOrgItems, stateFilter]);

  useEffect(() => {
    if (values.refresh) {
      setFieldValue('refresh', false);
      setStateFilter(undefined);
    }
    if (values.stateFilter) {
      setStateFilter(
        values.stateFilter === 'all' ? undefined : Number(values.stateFilter)
      );
    }
  }, [values, setFieldValue]);

  useEffect(() => {
    if (
      user?.preferences?.enquiryPresets &&
      designActivityTypeItems.length > 0 &&
      exActivityTypeItems.length > 0
    ) {
      setValues({
        ...user.preferences.enquiryPresets,
        refresh: true
      });
      setLoadedPastData(true);
    }
  }, [user, setValues, designActivityTypeItems, exActivityTypeItems]);

  useEffect(() => {
    if (status === 'deleted' || status === 'saved') {
      setTimeout(() => {
        dispatch(returnFromEnquiryPresets());
      }, 1000);
    }

    if (status === 'returning') {
      resetPresetsCache.current = false;
      history.push(APP_URL.LODGE_NEW_ENQUIRY);
    }
  }, [status, dispatch, history]);

  useComponentMount(() => {
    const params = {
      fields: 'id,name,organisationType',
      include: 'Tenant',
      forWOBO: true,
      limit: 1000
    };
    dispatch(fetchMemberStart(params));
    dispatch(fetchLodgeEnquiryInit());
    return () => {
      if (resetPresetsCache.current) {
        dispatch(clearEnquiryPresets());
      }
    };
  });

  return (
    <div
      id="enquiryPresets"
      data-testid="EnquiryPresets"
      className="enquiry-presets"
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Enquiry presets"
        pageDescription="Create presets to save time when lodging your enquiries"
        enableHeaderGlobalBar={true}
      >
        <>
          <div className="enquiry-presets-forms-container">
            <div className="enquiry-presets-form other-form">
              <div className="enquiry-presets-form-section">
                <section className="formFieldItem work-location">
                  {!values.refresh && (
                    <MultiSelect
                      id="workPlaceLoc"
                      size="xl"
                      titleText="Workplace location(s)"
                      items={workPlaceLocItems}
                      direction="bottom"
                      label="Choose options"
                      initialSelectedItems={getDefaultWorkPlaceLoc(
                        values,
                        workPlaceLocItems
                      )}
                      onChange={(selected: { selectedItems: any[] }) =>
                        handleWorkLocChange(selected)
                      }
                    />
                  )}
                </section>
                <section className="formFieldItem enquiry-type">
                  <span className="bx--label">Enquiry type</span>
                  <br />
                  <span className="bx--label txt-blue-60">
                    All enquiry types are now free
                  </span>
                  <div className="radio-group-container">
                    <RadioButtonGroup
                      labelPosition="right"
                      name="lodgeEnquiryType"
                      onChange={(val: React.ReactText) =>
                        handleLodgeEnquiryTypeChange(val)
                      }
                      valueSelected={values.lodgeEnquiryType}
                    >
                      <RadioButton
                        id="radio-design"
                        labelText="Design"
                        value="Design"
                        onBlur={handleBlur}
                      />
                      <RadioButton
                        id="radio-excavation"
                        labelText="Excavation"
                        value="Excavation"
                        onBlur={handleBlur}
                      />
                    </RadioButtonGroup>
                    <Button
                      kind="ghost"
                      size="small"
                      onClick={() => handleClearLodgeEnquiryType()}
                    >
                      Clear
                    </Button>
                  </div>
                  <div className="txt-container">
                    <Information16 />
                    <span className="bx--label">
                      If you&apos;re only doing design and do not currently have
                      excavation activity scheduled, please don&apos;t use
                      excavation
                    </span>
                  </div>
                  {designActivityTypeItems &&
                    !values.refresh &&
                    values.lodgeEnquiryType === 'Design' && (
                      <MultiSelect
                        id="activityTypes"
                        size="xl"
                        items={designActivityTypeItems}
                        direction="bottom"
                        label="Choose options"
                        initialSelectedItems={getDefaultActivity(
                          values,
                          designActivityTypeItems
                        )}
                        onChange={(selected: { selectedItems: any[] }) =>
                          handleActivityChange(selected)
                        }
                      />
                    )}
                  {exActivityTypeItems &&
                    !values.refresh &&
                    values.lodgeEnquiryType === 'Excavation' && (
                      <MultiSelect
                        id="activityTypes"
                        size="xl"
                        items={exActivityTypeItems}
                        direction="bottom"
                        label="Choose options"
                        initialSelectedItems={getDefaultActivity(
                          values,
                          exActivityTypeItems
                        )}
                        onChange={(selected: { selectedItems: any[] }) =>
                          handleActivityChange(selected)
                        }
                      />
                    )}
                </section>
              </div>
              <div className="enquiry-presets-form-section">
                <section className="formFieldItem">
                  <TextInput
                    id="userReference"
                    size="xl"
                    name="userReference"
                    labelText="Your reference"
                    data-testid="userReference"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={50}
                    value={values.userReference}
                  />
                </section>
                <section className="formFieldItem">
                  <TextArea
                    id="notes"
                    name="notes"
                    labelText="Notes"
                    onChange={handleChange}
                    data-testid="notes"
                    rows={3}
                    maxLength={500}
                    value={values.notes}
                  />
                </section>
              </div>
            </div>
            <div className="enquiry-presets-form wobo-form">
              <div className="enquiry-presets-form-section">
                <section className="formFieldItem authority-type">
                  <span className="bx--label">Working on behalf of</span>
                  <div className="radio-group-container">
                    <RadioButtonGroup
                      labelPosition="right"
                      name="authorityType"
                      onChange={(val: React.ReactText) =>
                        handleAuthorityTypeChange(val)
                      }
                      valueSelected={values.authorityType}
                      orientation={
                        screenSize === SCREEN_SIZE.SM
                          ? 'vertical'
                          : 'horizontal'
                      }
                    >
                      <RadioButton
                        id="radio-local"
                        labelText="Local authorities"
                        value="local"
                        onBlur={handleBlur}
                      />
                      <RadioButton
                        id="radio-utilities"
                        labelText="Utilities"
                        value="utilities"
                        onBlur={handleBlur}
                      />
                      <RadioButton
                        id="radio-other"
                        labelText="Other"
                        value="other"
                        onBlur={handleBlur}
                      />
                      <RadioButton
                        id="radio-private"
                        labelText="Private"
                        value="private"
                        onBlur={handleBlur}
                      />
                    </RadioButtonGroup>
                    <Button
                      kind="ghost"
                      size="small"
                      onClick={() => handleClearAuthorityType()}
                    >
                      Clear
                    </Button>
                  </div>
                </section>
                <section className="formFieldItem authority-value">
                  <div className="authority-input-conatainer">
                    {!values.refresh && getAuthorityInput(values.authorityType)}
                  </div>
                </section>
              </div>
            </div>
          </div>
          <div className="enquiry-presets-controls-container">
            <Toggle
              aria-label="always start button"
              toggled={values.alwaysStart}
              id="always-start-toggle"
              labelText="Always start new enquiries with these presets"
              onToggle={(checked) => {
                setFieldTouched('alwaysStart', true);
                setFieldValue('alwaysStart', checked);
              }}
            />
            <div className="buttons-container">
              <div className="button-group">
                <Button
                  className="red-btn"
                  kind="ghost"
                  disabled={
                    status === 'saving' ||
                    status === 'saved' ||
                    status === 'deleting' ||
                    status === 'deleted'
                  }
                  onClick={() => dispatch(deleteEnquiryPresetsStart())}
                >
                  {status === 'deleting' || status === 'deleted' ? (
                    <InlineLoading
                      description={
                        status === 'deleted' ? 'Deleted' : 'Deleting ...'
                      }
                      status={status === 'deleted' ? 'finished' : 'active'}
                      aria-live={'assertive'}
                    />
                  ) : (
                    'Delete presets'
                  )}
                </Button>
                <Button
                  kind="ghost"
                  disabled={
                    status === 'saving' ||
                    status === 'saved' ||
                    status === 'deleting' ||
                    status === 'deleted'
                  }
                  onClick={() => resetForm()}
                >
                  Reset
                </Button>
              </div>
              <div className="button-group">
                <Button
                  kind="tertiary"
                  disabled={
                    status === 'saving' ||
                    status === 'saved' ||
                    status === 'deleting' ||
                    status === 'deleted'
                  }
                  onClick={() => dispatch(returnFromEnquiryPresets())}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  disabled={
                    status === 'saving' ||
                    status === 'saved' ||
                    status === 'deleting' ||
                    status === 'deleted' ||
                    !canSave(values, touched, loadedPastData)
                  }
                  onClick={handleSubmit}
                >
                  {status === 'saving' || status === 'saved' ? (
                    <InlineLoading
                      description={status === 'saved' ? 'Saved' : 'Saving ...'}
                      status={status === 'saved' ? 'finished' : 'active'}
                      aria-live={'assertive'}
                    />
                  ) : (
                    'Save changes'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

const mapStateToProps = ({
  session,
  enquiresCache,
  membersCache,
  lodgeEnquiryCache
}: RootState) => ({
  user: session.currentUser,
  screenSize: session.screenSize,
  designAct: enquiresCache.data?.lodgeEnquiry?.designAct,
  excavationAct: enquiresCache.data?.lodgeEnquiry?.excavationAct,
  orgs: membersCache.selected,
  status: lodgeEnquiryCache.presets?.status
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default React.memo(connector(EnquiryPresets));
